import {
  all,
  call,
  put,
  select,
  StrictEffect,
  takeLatest
} from "redux-saga/effects";

import { DataType } from "@/jenesys-types/data/enums";
import { IBankStatementDocument } from "@/jenesys-types/jenesys-entities/config";

import { performDataAction } from "@/services/api";
import {
  selectActiveClientId,
  selectActiveCompanyId
} from "@/store/features/companies/companies";

import {
  downloadCSVFailure,
  downloadCSVFiles,
  downloadCSVStart,
  downloadCSVSuccess,
  downloadPDFFailure,
  downloadPDFFiles,
  downloadPDFStart,
  downloadPDFSuccess,
  loadStatementsFailure,
  loadStatementsStart,
  loadStatementsSuccess,
  uploadBankStatements,
  uploadStatementsFailure,
  uploadStatementsStart,
  uploadStatementsSuccess
} from "./bankStatementDocuments";

function* loadStatementsSaga(
  action: ReturnType<typeof loadStatementsStart>
): Generator<any, void, any> {
  const { clientId, search } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    const response = yield call(
      performDataAction,
      DataType.BankStatementDocument,
      "getStatementsWithParams",
      {
        companyId: clientId || companyId,
        search
      }
    );
    yield put(loadStatementsSuccess(response));
  } catch (error: any) {
    yield put(loadStatementsFailure(error.message));
  }
}

function* uploadStatementsSaga(
  action: ReturnType<typeof uploadStatementsStart>
): Generator<StrictEffect, void, any> {
  const { files } = action.payload;
  const clientId = yield select(selectActiveClientId);
  const companyId = yield select(selectActiveCompanyId);
  const formData = new FormData();
  files.forEach((file: File) => {
    formData.append("files", file, file.name);
  });

  try {
    yield call(
      performDataAction,
      DataType.BankStatementDocument,
      "create",
      { companyId: clientId || companyId },
      undefined,
      formData
    );
    yield put(uploadStatementsSuccess());
    yield put(loadStatementsStart({ clientId, search: "" }));
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "An unknown error occurred";
    yield put(uploadStatementsFailure(errorMessage));
  }
}

function* downloadCSVSaga(
  action: ReturnType<typeof downloadCSVStart>
): Generator<any, void, any> {
  const { clientId, ids } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    yield call(
      performDataAction,
      DataType.BankStatementDocument,
      "downloadCSV",
      { companyId: clientId || companyId },
      undefined,
      { ids }
    );
    yield put(downloadCSVSuccess());
  } catch (error: any) {
    yield put(downloadCSVFailure(error.message));
  }
}

function* downloadPDFSaga(
  action: ReturnType<typeof downloadPDFStart>
): Generator<any, void, any> {
  const { clientId, statementId } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    yield call(
      performDataAction,
      DataType.BankStatementDocument,
      "downloadPDF",
      {
        companyId: clientId || companyId,
        statementId
      }
    );
    yield put(downloadPDFSuccess());
  } catch (error: any) {
    yield put(downloadPDFFailure(error.message));
  }
}

function* handleUploadStatementsSaga(
  action: ReturnType<typeof uploadBankStatements>
): Generator<StrictEffect, void, any> {
  const { files } = action.payload;
  const clientId = yield select(selectActiveClientId);
  const companyId = yield select(selectActiveCompanyId);
  const formData = new FormData();
  files.forEach((file: File) => {
    formData.append("files", file, file.name);
  });

  try {
    yield call(
      performDataAction,
      DataType.BankStatementDocument,
      "create",
      { companyId: clientId || companyId },
      undefined,
      formData
    );
    yield put(uploadStatementsSuccess());
    yield put(loadStatementsStart({ clientId, search: "" }));
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "An unknown error occurred";
    yield put(uploadStatementsFailure(errorMessage));
  }
}

function* downloadPDFFile(statement: IBankStatementDocument, clientId: string) {
  yield put(downloadPDFStart({ clientId, statementId: statement.id }));
  const link = document.createElement("a");
  link.href = statement.download_pdf_url;
  link.download = statement.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  yield put(downloadPDFSuccess());
}

function* downloadCSVFile(statement: IBankStatementDocument, clientId: string) {
  yield put(downloadCSVStart({ clientId, ids: [statement.id] }));
  const link = document.createElement("a");
  link.href = statement.download_csv_url;
  link.download = statement.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  yield put(downloadCSVSuccess());
}

function* handleDownloadPDFFilesSaga(
  action: ReturnType<typeof downloadPDFFiles>
): Generator<StrictEffect, void, any> {
  const { statements } = action.payload;
  const clientId = yield select(selectActiveClientId);

  try {
    yield all(
      statements.map((statement) => call(downloadPDFFile, statement, clientId))
    );
  } catch (error: any) {
    yield put(downloadPDFFailure(error.message));
  }
}

function* handleDownloadCSVFilesSaga(
  action: ReturnType<typeof downloadCSVFiles>
): Generator<StrictEffect, void, any> {
  const { statements } = action.payload;
  const clientId = yield select(selectActiveClientId);

  try {
    const downloadableStatements = statements.filter(
      (statement) => statement.download_csv_url
    );
    yield all(
      downloadableStatements.map((statement) =>
        call(downloadCSVFile, statement, clientId)
      )
    );
  } catch (error: any) {
    yield put(downloadCSVFailure(error.message));
  }
}

export default function* bankStatementDocumentsSaga() {
  yield takeLatest(loadStatementsStart.type, loadStatementsSaga);
  yield takeLatest(uploadStatementsStart.type, uploadStatementsSaga);
  yield takeLatest(downloadCSVStart.type, downloadCSVSaga);
  yield takeLatest(downloadPDFStart.type, downloadPDFSaga);
  yield takeLatest(uploadBankStatements.type, handleUploadStatementsSaga);
  yield takeLatest(downloadPDFFiles.type, handleDownloadPDFFilesSaga);
  yield takeLatest(downloadCSVFiles.type, handleDownloadCSVFilesSaga);
}
